import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AuditAPI, extractError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

export default function AuditPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await AuditAPI.list({ limit: 100 });
                setItems(r.items);
            } catch (e) {
                toast.error(extractError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="audit-page">
            <header>
                <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Audit log</h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Every financial / data-changing write captured with before/after diffs and request IDs.
                </p>
            </header>
            <Card className="overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Time</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Entity</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Action</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Actor</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Request ID</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-6 text-center text-sm text-zinc-500">
                                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-6 text-center text-sm text-zinc-500">
                                    No audit entries yet.
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((it) => (
                            <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                                <TableCell className="font-mono text-xs">{formatDateTime(it.created_at)}</TableCell>
                                <TableCell className="font-medium">{it.entity}</TableCell>
                                <TableCell>
                                    <span className="rounded border border-brand-redSoft bg-brand-redSoft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-redDeep dark:border-brand-red/40 dark:bg-brand-red/10 dark:text-brand-red">
                                        {it.action}
                                    </span>
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                    {it.actor_user_id ? it.actor_user_id.slice(-6) : "—"}
                                </TableCell>
                                <TableCell className="font-mono text-[10px] text-zinc-400">
                                    {it.request_id || "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
